import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button";
import { Copy, Check, RefreshCw, FileText } from "lucide-react";

export default function MessageBubble({
  message,
  onCopy,
  onRegenerate,
  isLast = false,
  isStreaming = false,
}) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";

  const handleCopy = async () => {
    await onCopy?.(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isUser) {
    return (
      <div className="flex justify-end message-enter" data-testid={`message-${message.message_id}`}>
        <div className="bg-[#1E1E1E] text-white rounded-2xl rounded-tr-sm px-6 py-4 max-w-[85%] shadow-sm">
          {/* Show attached file indicator */}
          {message.file_info && (
            <div className="flex items-center gap-2 bg-[#0D0D0D] border border-[#333] rounded-lg px-3 py-2 mb-3 text-sm">
              <FileText className="w-4 h-4 text-[#14A800]" />
              <span className="text-[#E8E8E8] truncate">{message.file_info.filename}</span>
            </div>
          )}
          <p className="text-base leading-relaxed whitespace-pre-wrap">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 message-enter" data-testid={`message-${message.message_id}`}>
      {/* ZOEY Avatar */}
      <div className="w-8 h-8 rounded-full bg-[#14A800] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(20,168,0,0.2)]">
        <span className="text-white font-bold text-sm font-serif">Z</span>
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        <div className="ai-message-content text-[#EDEDED] font-serif text-base leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
              h1: ({ children }) => <h1 className="text-xl font-bold mb-4 mt-6 first:mt-0">{children}</h1>,
              h2: ({ children }) => <h2 className="text-lg font-bold mb-3 mt-5 first:mt-0">{children}</h2>,
              h3: ({ children }) => <h3 className="text-base font-bold mb-2 mt-4 first:mt-0">{children}</h3>,
              ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
              li: ({ children }) => <li className="text-[#EDEDED]">{children}</li>,
              code: ({ inline, children }) => {
                if (inline) {
                  return (
                    <code className="bg-[#1E1E1E] px-1.5 py-0.5 rounded text-sm font-mono text-[#14A800]">
                      {children}
                    </code>
                  );
                }
                return (
                  <code className="block bg-[#1E1E1E] p-4 rounded-lg text-sm font-mono overflow-x-auto">
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre className="bg-[#1E1E1E] rounded-lg overflow-x-auto mb-4">
                  {children}
                </pre>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-[#14A800] pl-4 my-4 text-[#A1A1AA] italic">
                  {children}
                </blockquote>
              ),
              a: ({ href, children }) => (
                <a 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#14A800] hover:underline"
                >
                  {children}
                </a>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border border-[#27272A] rounded-lg">
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th className="border border-[#27272A] px-4 py-2 bg-[#1E1E1E] text-left font-semibold">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="border border-[#27272A] px-4 py-2">
                  {children}
                </td>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
          
          {/* Typing cursor for streaming */}
          {isStreaming && <span className="typing-cursor" />}
        </div>

        {/* Action buttons - only show when not streaming */}
        {!isStreaming && (
          <div className="flex items-center gap-2 mt-3 opacity-0 hover:opacity-100 transition-opacity group-hover:opacity-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-8 px-2 text-[#A1A1AA] hover:text-white hover:bg-[#1E1E1E]"
              data-testid={`copy-btn-${message.message_id}`}
            >
              {copied ? (
                <Check className="w-4 h-4 mr-1 text-[#14A800]" />
              ) : (
                <Copy className="w-4 h-4 mr-1" />
              )}
              <span className="text-xs">{copied ? "Copied" : "Copy"}</span>
            </Button>
            
            {isLast && onRegenerate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRegenerate(message.message_id)}
                className="h-8 px-2 text-[#A1A1AA] hover:text-white hover:bg-[#1E1E1E]"
                data-testid={`regenerate-btn-${message.message_id}`}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                <span className="text-xs">Regenerate</span>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
